/**
 * NodeEditor — Side panel editor logic
 * Per PLAN.md interactions/NodeEditor.js and PRD §5.4
 */

import { eventBus, Events } from '../core/EventBus.js';
import { debounce } from '../utils/debounce.js';
import { LATEX_DEBOUNCE } from '../utils/constants.js';

export class NodeEditor {
    /**
     * @param {import('../models/CanvasDocument.js').CanvasDocument} document 
     * @param {import('../core/Canvas.js').Canvas} canvas 
     */
    constructor(document, canvas) {
        this.document = document;
        this.canvas = canvas;
        this.currentNodeId = null;
        this.originalContent = '';

        // Debounced preview update
        this.updatePreview = debounce(this._updatePreview.bind(this), LATEX_DEBOUNCE);

        this.init();
    }

    init() {
        // Listen for editor open requests
        eventBus.on(Events.EDITOR_OPEN, ({ nodeId }) => {
            this.open(nodeId);
        });

        eventBus.on(Events.EDITOR_CLOSE, () => {
            this.close();
        });
    }

    /**
     * Open editor for a node
     * @param {string} nodeId 
     */
    open(nodeId) {
        const node = this.document.getNode(nodeId);
        if (!node) return;

        this.currentNodeId = nodeId;
        this.originalContent = node.content;

        // Emit event for UI to show editor
        eventBus.emit('editor:show', {
            nodeId,
            content: node.content,
            type: node.type,
            position: this.getEditorPosition(node)
        });
    }

    /**
     * Get editor position (to the right of node)
     * @param {Node} node 
     */
    getEditorPosition(node) {
        const bounds = node.getBounds();
        const screen = this.canvas.viewport.worldToScreen(
            bounds.x + bounds.width,
            bounds.y
        );

        return {
            x: screen.x + 20,
            y: screen.y
        };
    }

    /**
     * Update node content (called on input)
     * @param {string} content 
     */
    setContent(content) {
        if (!this.currentNodeId) return;

        const node = this.document.getNode(this.currentNodeId);
        if (node) {
            node.content = content;
            this.updatePreview();
        }
    }

    /**
     * Update node type
     * @param {'latex'|'text'} type 
     */
    setType(type) {
        if (!this.currentNodeId) return;

        const node = this.document.getNode(this.currentNodeId);
        if (node) {
            node.type = type;
            eventBus.emit(Events.RENDER_REQUEST);
        }
    }

    /**
     * Internal preview update (debounced)
     */
    _updatePreview() {
        // Emit NODE_UPDATE to trigger full render path (not just position update)
        eventBus.emit(Events.NODE_UPDATE, { nodeId: this.currentNodeId });
        eventBus.emit('editor:preview', { nodeId: this.currentNodeId });
    }

    /**
     * Confirm edit and close
     * @returns {{nodeId: string, oldContent: string, newContent: string}|null}
     */
    confirm() {
        if (!this.currentNodeId) return null;

        const node = this.document.getNode(this.currentNodeId);
        if (!node) return null;

        const result = {
            nodeId: this.currentNodeId,
            oldContent: this.originalContent,
            newContent: node.content
        };

        this.close();
        return result;
    }

    /**
     * Cancel edit and revert
     */
    cancel() {
        if (!this.currentNodeId) return;

        const node = this.document.getNode(this.currentNodeId);
        if (node) {
            node.content = this.originalContent;
        }

        this.close();
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Close editor
     */
    close() {
        this.currentNodeId = null;
        this.originalContent = '';
        eventBus.emit('editor:hide', {});
    }

    /**
     * Check if editor is open
     * @returns {boolean}
     */
    isOpen() {
        return this.currentNodeId !== null;
    }

    /**
     * Get current editing node ID
     * @returns {string|null}
     */
    getCurrentNodeId() {
        return this.currentNodeId;
    }
}
